import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { produtoService, Produto } from '../../services/produto';

const ProdutoVisualizar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: produto, isLoading, error } = useQuery({
    queryKey: ['produto', id],
    queryFn: () => produtoService.getById(Number(id)),
    enabled: !!id,
  });

  const handleBack = () => {
    navigate('/produtos');
  };

  const handleEdit = () => {
    navigate(`/produtos/editar/${id}`);
  };

  const handleUnificar = () => {
    navigate(`/produtos/unificar/${id}`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Erro ao carregar os dados do produto.
        </Alert>
      </Box>
    );
  }

  if (!produto) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Produto não encontrado.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Visualizar Produto
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            color="primary"
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            startIcon={<CompareArrowsIcon />}
            onClick={handleUnificar}
          >
            Unificar
          </Button>
        </Box>
      </Box>

      {/* Conteúdo */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Informações Básicas */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Informações Básicas
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Código
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.id}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Nome do Produto
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.nomeProduto || 'N/A'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Descrição
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.descricao || 'N/A'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Referência
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
                    {produto.referencia || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Informações de Venda */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Informações de Venda
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Preço de Venda
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {formatCurrency(produto.precoVenda)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Comissão Personalizada
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.comissaoPersonalizada}%
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Sem Comissão
                  </Typography>
                  <Chip
                    label={produto.semComissao ? 'Sim' : 'Não'}
                    color={produto.semComissao ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    % Vendas (30 dias)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.porcentagemVendasMesAnterior !== undefined 
                      ? formatPercentage(produto.porcentagemVendasMesAnterior)
                      : 'N/A'
                    }
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Informações de Estoque */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Informações de Estoque
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Quantidade Consignada
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.quantidadeNasPracas || 0}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Quantidade Disponível
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.quantidadeDisponível || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Informações de Classificação */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Classificação
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Código da Marca
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.codMarca || 'N/A'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Código da Linha
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {produto.codLinha || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProdutoVisualizar;
